/**
 * Aisle/bay location box with a tooltip explaining how to spot the bay signage in-store.
 * Extracted from `PhysicalStoreInventory` per the Availability Store Location spec (US-160133).
 */
import {
	MSI_PROMO_ESPOT,
	PDP_AISLE_BAY,
	PRO_PDP_AISLE_BAY,
} from '@/components/blocks/ProductDetails/constants';
import {
	availabilityStoreLocationSx,
	physicalStoreInventorySx,
} from '@/components/blocks/ProductDetails/styles/inventoryAvailability';
import { EMarketingSpot } from '@/components/content/EMarketingSpot';
import { PinDropIcon } from '@/components/icons/svg/PinDrop';
import { useLocalization } from '@/data/Localization';
import { textTransformToSequence } from '@/utils/commons';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import { Box, Tooltip, Typography } from '@mui/material';
import { FC } from 'react';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

interface Props {
	isB2B: boolean;
	bayData: { aisle: string; area: string; bay: string };
}

export const AvailabilityStoreLocation: FC<Props> = ({ isB2B, bayData }) => {
	const { Availability } = useLocalization('HDMProductDetail');
	const isAisleNumeric = /^\d+$/.test(bayData?.aisle ?? '');

	const buildLocationLabel = (label: string, value: string) => (
		<>
			<Typography variant="bodyBaseline" color="grey.500" component="span">
				{label}
			</Typography>
			<Typography
				variant="bodyBaseline"
				fontWeight={OpenSansFontWeights.semibold}
				color="grey.500"
				component="span"
			>
				{value}
			</Typography>
		</>
	);

	return (
		<Box sx={availabilityStoreLocationSx.container} id="storeAvailabilityLocation">
			<PinDropIcon variant="filled" sx={availabilityStoreLocationSx.icon} />
			{buildLocationLabel(
				isAisleNumeric ? Availability.labels.aisle.t() : Availability.labels.area.t(),
				isAisleNumeric ? bayData.aisle : textTransformToSequence(bayData.area)
			)}
			<Typography variant="bodyBaseline" color="grey.500" component="span">
				{' • '}
			</Typography>
			{buildLocationLabel(Availability.labels.bay.t(), bayData.bay)}
			<Tooltip
				title={
					<Box className="store-availability-card__tooltip-content">
						<EMarketingSpot
							id={MSI_PROMO_ESPOT}
							properties={{ emsName: isB2B ? PRO_PDP_AISLE_BAY : PDP_AISLE_BAY }}
						/>
					</Box>
				}
				placement="bottom-start"
				enterTouchDelay={0}
				sx={physicalStoreInventorySx.tooltipSx}
			>
				<HelpCenterIcon id="storeAvailabilityTooltip" sx={physicalStoreInventorySx.helpIcon} />
			</Tooltip>
		</Box>
	);
};
